import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { BookOpen, Download, FileText, Loader2, Upload, RefreshCw, Eye, Trash2, Edit } from 'lucide-react';

// Helper function to get authenticated headers
const getAuthHeaders = (additionalHeaders = {}) => {
  const token = localStorage.getItem('auth_token');
  return {
    'Authorization': `Bearer ${token}`,
    ...additionalHeaders
  };
};

// Subject options based on education level
const SUBJECTS = {
  kg: [
    'Language & Literacy',
    'Numeracy', 
    'Creative Arts'
  ],
  primary: [
    'English',
    'Science',
    'Mathematics',
    'History',
    'Computing',
    'Religious and moral education',
    'Creative Arts',
    'Ghanaian language',
    'French'
  ],
  jhs: [
    'English',
    'Science',
    'Mathematics',
    'Social Studies',
    'Computing',
    'Career Technology',
    'Creative Arts',
    'Religious and moral education',
    'Ghanaian language',
    'French'
  ]
};

const CLASSES = {
  kg: ['KG 1', 'KG 2'],
  primary: ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6'],
  jhs: ['JHS 1', 'JHS 2', 'JHS 3']
};

// Validation schema - term and academic_year removed as they come from active term settings
const lessonGenerationSchema = z.object({
  education_level: z.enum(['kg', 'primary', 'jhs'], { 
    required_error: "Please select an education level" 
  }),
  class_name: z.string().min(1, "Please select a class"),
  subjects: z.array(z.string()).optional(),
  subject: z.string().optional(),
  strand: z.string().min(5, "Strand must be at least 5 characters").max(200, "Strand too long"),
  sub_strand: z.string().min(5, "Sub-strand must be at least 5 characters").max(200, "Sub-strand too long"),
  content_standard: z.string().min(10, "Content standard must be at least 10 characters").max(500, "Content standard too long"),
  indicator: z.string().min(10, "Indicator must be at least 10 characters").max(500, "Indicator too long")
}).refine((data) => {
  // For JHS, subject is required; for KG/Primary, subjects array is required
  if (data.education_level === 'jhs') {
    return data.subject && data.subject.length > 0;
  } else {
    return data.subjects && data.subjects.length > 0;
  }
}, {
  message: "Please select at least one subject",
  path: ["subjects"] // This will show the error on subjects field
});

const AILessonGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedNotes, setGeneratedNotes] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadedNotes, setUploadedNotes] = useState([]);
  const [showUploadLogs, setShowUploadLogs] = useState(false);
  const [activeTerm, setActiveTerm] = useState(null);
  const [loadingTerm, setLoadingTerm] = useState(true);
  const { toast } = useToast();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(lessonGenerationSchema),
    defaultValues: {
      education_level: '',
      class_name: '',
      subjects: [],
      subject: '',
      strand: '',
      sub_strand: '',
      content_standard: '',
      indicator: ''
    }
  });

  const watchedEducationLevel = watch('education_level');

  // Fetch active term on component mount
  useEffect(() => {
    const fetchActiveTerm = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${backendUrl}/api/admin/terms/active`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          setActiveTerm(data.term);
        }
      } catch (error) {
        console.error('Error fetching active term:', error);
      } finally {
        setLoadingTerm(false);
      }
    };
    
    fetchActiveTerm();
  }, []);

  // Reset form when education level changes
  useEffect(() => {
    if (watchedEducationLevel) {
      setValue('class_name', '');
      setValue('subjects', []);
      setValue('subject', '');
      setSelectedSubjects([]);
    }
  }, [watchedEducationLevel, setValue]);

  const handleSubjectToggle = (subject) => {
    const currentSubjects = selectedSubjects;
    const updatedSubjects = currentSubjects.includes(subject)
      ? currentSubjects.filter(s => s !== subject)
      : [...currentSubjects, subject];
    
    setSelectedSubjects(updatedSubjects);
    setValue('subjects', updatedSubjects);
  };

  const onSubmit = async (data) => {
    // Validate that we have active term with required metadata
    if (!activeTerm) {
      toast({
        title: "Error",
        description: "No active term found. Please set an active term in Term Management.",
        variant: "destructive"
      });
      return;
    }
    
    if (!activeTerm.academic_year || !activeTerm.current_week || !activeTerm.week_ending_date) {
      toast({
        title: "Missing Term Settings",
        description: "Please set Academic Year, Week Number, and Week Ending Date in Term Management first.",
        variant: "destructive"
      });
      return;
    }
    
    setIsGenerating(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Merge form data with active term metadata
      const requestData = {
        ...data,
        term: activeTerm.name.includes('1') ? '1' : activeTerm.name.includes('2') ? '2' : '3',
        week: activeTerm.current_week,
        academic_year: activeTerm.academic_year,
        week_ending: activeTerm.week_ending_date
      };
      
      console.log('🚀 Starting lesson note generation:', requestData);
      
      const response = await fetch(`${backendUrl}/api/admin/generate-lesson-notes`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        credentials: 'include',
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Generation failed:', errorData);
        
        // Show specific error messages based on the error type
        let errorMessage = 'Failed to generate lesson notes';
        if (errorData.detail) {
          if (errorData.detail.includes('API key')) {
            errorMessage = 'AI service configuration issue. Please contact administrator.';
          } else if (errorData.detail.includes('rate limit')) {
            errorMessage = 'Too many requests. Please wait a moment and try again.';
          } else if (errorData.detail.includes('Lesson note generation failed')) {
            errorMessage = 'AI generation failed. Please check your inputs and try again.';
          } else {
            errorMessage = errorData.detail;
          }
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Generation successful:', result);
      
      setGeneratedNotes(prev => [result, ...prev]);
      
      toast({
        title: "Success!",
        description: `Generated ${result.pdf_urls?.length || 1} lesson note(s) successfully for ${data.education_level.toUpperCase()} - ${data.class_name}`,
        variant: "default"
      });

      // Reset form
      reset();
      setSelectedSubjects([]);

    } catch (error) {
      console.error('Error generating lesson notes:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate lesson notes",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const loadGeneratedNotes = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/admin/generated-lesson-notes`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const notes = await response.json();
        setGeneratedNotes(notes);
      }
    } catch (error) {
      console.error('Error loading generated notes:', error);
    }
  };

  useEffect(() => {
    loadGeneratedNotes();
  }, []);

  // Upload lesson note to teacher access
  const uploadToTeacherAccess = async (note) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/admin/upload-lesson-to-teachers`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        credentials: 'include',
        body: JSON.stringify({
          lesson_note_id: note.id,
          class_name: note.class_name,
          subject: note.subject,
          education_level: note.education_level,
          term: note.term,
          week: note.week,
          academic_year: note.academic_year,
          lesson_content: note.lesson_content || note.content || '', // CRITICAL: Include lesson content for PDF generation
        })
      });

      if (response.ok) {
        toast({
          title: "Success!",
          description: "Lesson note uploaded to teacher access successfully",
        });
        loadUploadedNotes(); // Refresh upload logs
      } else {
        throw new Error('Failed to upload lesson note');
      }
    } catch (error) {
      console.error('Error uploading lesson note:', error);
      toast({
        title: "Error",
        description: "Failed to upload lesson note to teacher access",
        variant: "destructive"
      });
    }
  };

  // Rewrite lesson note using AI
  const rewriteLessonNote = async (note) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      setIsGenerating(true);
      
      const response = await fetch(`${backendUrl}/api/admin/rewrite-lesson-note`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        credentials: 'include',
        body: JSON.stringify({
          original_note_id: note.id,
          rewrite_instruction: "Please rewrite this lesson note with improved content and structure"
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Add rewritten note to the list
        setGeneratedNotes(prev => [result, ...prev]);
        
        toast({
          title: "Success!",
          description: "Lesson note rewritten successfully",
        });
      } else {
        throw new Error('Failed to rewrite lesson note');
      }
    } catch (error) {
      console.error('Error rewriting lesson note:', error);
      toast({
        title: "Error", 
        description: "Failed to rewrite lesson note",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Download to class folder (for KG/Primary)
  const downloadToClassFolder = async (note) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Generate class folder name: 'Class-Term-week-lesson note'
      const folderName = `${note.class_name}-Term_${note.term}-Week_${note.week}-lesson_note`;
      
      const response = await fetch(`${backendUrl}/api/admin/download-class-folder/${note.id}?folder_name=${encodeURIComponent(folderName)}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${folderName}.zip`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast({
          title: "Success!",
          description: `Class folder downloaded: ${folderName}.zip`,
        });
      } else {
        throw new Error('Failed to download class folder');
      }
    } catch (error) {
      console.error('Error downloading class folder:', error);
      toast({
        title: "Error",
        description: "Failed to download class folder",
        variant: "destructive"
      });
    }
  };

  // Load uploaded notes for logs
  const loadUploadedNotes = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/admin/uploaded-lesson-notes`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const notes = await response.json();
        setUploadedNotes(notes);
      }
    } catch (error) {
      console.error('Error loading uploaded notes:', error);
    }
  };

  // Delete generated lesson note
  const deleteGeneratedNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this generated lesson note? This action cannot be undone.')) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/admin/generated-lesson-notes/${noteId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast({
          title: "Success!",
          description: "Generated lesson note deleted successfully",
        });
        // Refresh the generated notes list
        loadGeneratedNotes();
      } else {
        throw new Error('Failed to delete generated lesson note');
      }
    } catch (error) {
      console.error('Error deleting generated lesson note:', error);
      toast({
        title: "Error",
        description: "Failed to delete generated lesson note",
        variant: "destructive"
      });
    }
  };

  // Delete uploaded note
  const deleteUploadedNote = async (noteId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/admin/uploaded-lesson-notes/${noteId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast({
          title: "Success!",
          description: "Uploaded lesson note deleted successfully",
        });
        loadUploadedNotes(); // Refresh the list
      } else {
        throw new Error('Failed to delete uploaded note');
      }
    } catch (error) {
      console.error('Error deleting uploaded note:', error);
      toast({
        title: "Error",
        description: "Failed to delete uploaded lesson note",
        variant: "destructive"
      });
    }
  };

  // Re-upload note with new name
  const reUploadNote = async (noteId, newName) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/admin/re-upload-lesson-note`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        credentials: 'include',
        body: JSON.stringify({
          note_id: noteId,
          new_name: newName
        })
      });

      if (response.ok) {
        toast({
          title: "Success!",
          description: "Lesson note re-uploaded with new name successfully",
        });
        loadUploadedNotes(); // Refresh the list
      } else {
        throw new Error('Failed to re-upload lesson note');
      }
    } catch (error) {
      console.error('Error re-uploading lesson note:', error);
      toast({
        title: "Error",
        description: "Failed to re-upload lesson note",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            AI Lesson Note Generator
          </CardTitle>
          <CardDescription>
            Generate comprehensive lesson notes using Gemini 2.5 AI following GES Standard-Based Curriculum - Updated Interface
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Education Level Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Education Level</label>
                <Controller
                  name="education_level"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">Kindergarten (KG)</SelectItem>
                        <SelectItem value="primary">Primary School</SelectItem>
                        <SelectItem value="jhs">Junior High School (JHS)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.education_level && (
                  <p className="text-sm text-red-500 mt-1">{errors.education_level.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Class</label>
                <Controller
                  name="class_name"
                  control={control}
                  render={({ field }) => (
                    <Select 
                      onValueChange={field.onChange} 
                      value={field.value}
                      disabled={!watchedEducationLevel}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select class" />
                      </SelectTrigger>
                      <SelectContent>
                        {watchedEducationLevel && CLASSES[watchedEducationLevel]?.map((className) => (
                          <SelectItem key={className} value={className}>
                            {className}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.class_name && (
                  <p className="text-sm text-red-500 mt-1">{errors.class_name.message}</p>
                )}
              </div>
            </div>

            {/* Active Term Settings Display - Auto-populated from Term Management */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-800">Lesson Note Settings (Auto-populated)</span>
              </div>
              
              {loadingTerm ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading term settings...
                </div>
              ) : activeTerm ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 block">Term</span>
                    <span className="font-medium text-gray-900">{activeTerm.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Academic Year</span>
                    <span className="font-medium text-gray-900">
                      {activeTerm.academic_year || <span className="text-orange-600">Not set</span>}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Week</span>
                    <span className="font-medium text-gray-900">
                      {activeTerm.current_week ? `Week ${activeTerm.current_week}` : <span className="text-orange-600">Not set</span>}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Week Ending</span>
                    <span className="font-medium text-gray-900">
                      {activeTerm.week_ending_date || <span className="text-orange-600">Not set</span>}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-orange-600">
                  No active term found. Please activate a term in Term Management.
                </div>
              )}
              
              {activeTerm && (!activeTerm.academic_year || !activeTerm.current_week || !activeTerm.week_ending_date) && (
                <p className="text-orange-600 text-xs mt-2">
                  ⚠️ Please complete all settings in Term Management before generating lesson notes.
                </p>
              )}
            </div>

            {/* Subject Selection */}
            {watchedEducationLevel && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  {watchedEducationLevel === 'jhs' ? 'Subject' : 'Subjects (Select Multiple)'}
                </label>

                {watchedEducationLevel === 'jhs' ? (
                  <Controller
                    name="subject"
                    control={control}
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select subject" />
                        </SelectTrigger>
                        <SelectContent>
                          {SUBJECTS[watchedEducationLevel].map((subject) => (
                            <SelectItem key={subject} value={subject}>
                              {subject}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {SUBJECTS[watchedEducationLevel].map((subject) => (
                      <label
                        key={subject}
                        className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSubjects.includes(subject)}
                          onChange={() => handleSubjectToggle(subject)}
                          className="rounded"
                        />
                        <span className="text-sm">{subject}</span>
                      </label>
                    ))}
                  </div>
                )}
                
                {errors.subjects && (
                  <p className="text-sm text-red-500 mt-1">{errors.subjects.message}</p>
                )}
                {errors.subject && (
                  <p className="text-sm text-red-500 mt-1">{errors.subject.message}</p>
                )}
              </div>
            )}

            {/* Content Fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Strand</label>
                <Controller
                  name="strand"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter the curriculum strand"
                    />
                  )}
                />
                {errors.strand && (
                  <p className="text-sm text-red-500 mt-1">{errors.strand.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Sub-strand</label>
                <Controller
                  name="sub_strand"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter the curriculum sub-strand"
                    />
                  )}
                />
                {errors.sub_strand && (
                  <p className="text-sm text-red-500 mt-1">{errors.sub_strand.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Content Standard</label>
                <Controller
                  name="content_standard"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      placeholder="Enter the content standard from the curriculum"
                      rows={3}
                    />
                  )}
                />
                {errors.content_standard && (
                  <p className="text-sm text-red-500 mt-1">{errors.content_standard.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Indicator</label>
                <Controller
                  name="indicator"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      placeholder="Enter the performance indicator"
                      rows={3}
                    />
                  )}
                />
                {errors.indicator && (
                  <p className="text-sm text-red-500 mt-1">{errors.indicator.message}</p>
                )}
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={isGenerating} 
              className="w-full"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating Lesson Notes...
                </>
              ) : (
                <>
                  <BookOpen className="h-4 w-4 mr-2" />
                  Generate Lesson Notes
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Generated Notes Display */}
      {generatedNotes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Generated Lesson Notes
            </CardTitle>
            <CardDescription>
              Recently generated lesson notes - preview, upload, or download
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {generatedNotes.map((note) => (
                <div key={note.id} className="p-4 border rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-medium">
                        {/* New file naming format: JHS 1 – Religious and moral education – Term_Number – week_number – yyyy/yyyy */}
                        {note.class_name} – {note.subject || 'Multiple Subjects'} – Term_{note.term} – Week_{note.week} – {note.academic_year || `${note.year}/${parseInt(note.year) + 1}`}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {note.education_level.toUpperCase()} • Generated: {new Date(note.created_at).toLocaleString()}
                      </p>
                    </div>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                      {note.pdf_urls?.length || 1} PDF{(note.pdf_urls?.length || 1) > 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600 mb-3">
                    <p><strong>Strand:</strong> {note.strand}</p>
                    <p><strong>Sub-strand:</strong> {note.sub_strand}</p>
                  </div>

                  {/* Dynamic Action Buttons based on Education Level */}
                  <div className="flex gap-2 flex-wrap">
                    {/* Preview Button - Always Available */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedNote(note);
                        setShowPreview(true);
                      }}
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      Preview
                    </Button>

                    {/* Delete Button - Always Available */}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteGeneratedNote(note.id)}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>

                    {/* JHS: Preview → Upload or Rewrite */}
                    {note.education_level === 'jhs' && (
                      <>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => uploadToTeacherAccess(note)}
                        >
                          <Upload className="h-3 w-3 mr-1" />
                          Upload to Teachers
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => rewriteLessonNote(note)}
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Rewrite
                        </Button>
                      </>
                    )}

                    {/* KG/Primary: Preview → Download/Drop, Rewrite, Upload */}
                    {(note.education_level === 'kg' || note.education_level === 'primary') && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadToClassFolder(note)}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download to Class Folder
                        </Button>
                        <Button
                          variant="secondary" 
                          size="sm"
                          onClick={() => rewriteLessonNote(note)}
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Rewrite
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => uploadToTeacherAccess(note)}
                        >
                          <Upload className="h-3 w-3 mr-1" />
                          Upload to Teachers
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Logs */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload Logs
              </CardTitle>
              <CardDescription>
                Lesson notes uploaded to teacher access - manage and track uploads
              </CardDescription>
            </div>
            <Button variant="outline" onClick={loadUploadedNotes}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {uploadedNotes.length > 0 ? (
            <div className="space-y-4">
              {/* Group by class */}
              {Object.entries(
                uploadedNotes.reduce((acc, note) => {
                  const key = `${note.class_name} - ${note.education_level.toUpperCase()}`;
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(note);
                  return acc;
                }, {})
              ).map(([className, classNotes]) => (
                <div key={className} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium">{className}</h4>
                    <Badge variant="secondary">{classNotes.length} notes</Badge>
                  </div>
                  
                  <div className="space-y-2">
                    {classNotes.map((note) => (
                      <div key={note.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <p className="text-sm font-medium">
                            {note.subject} - Week {note.week}
                          </p>
                          <p className="text-xs text-gray-500">
                            Uploaded: {new Date(note.uploaded_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const newName = prompt('Enter new name for lesson note:', `${note.subject} - Week ${note.week}`);
                              if (newName && newName.trim()) {
                                reUploadNote(note.id, newName.trim());
                              }
                            }}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Re-upload
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this uploaded lesson note? This action cannot be undone.')) {
                                deleteUploadedNote(note.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Upload className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No lesson notes uploaded yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Lesson Note Preview</DialogTitle>
            <DialogDescription>
              {selectedNote && `${selectedNote.class_name} – ${selectedNote.subject || 'Multiple Subjects'} – Term ${selectedNote.term} – Week ${selectedNote.week}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {selectedNote && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Lesson Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p><strong>Class:</strong> {selectedNote.class_name}</p>
                      <p><strong>Subject:</strong> {selectedNote.subject || 'Multiple'}</p>
                      <p><strong>Term:</strong> {selectedNote.term}</p>
                    </div>
                    <div>
                      <p><strong>Week:</strong> {selectedNote.week}</p>
                      <p><strong>Year:</strong> {selectedNote.academic_year || selectedNote.year}</p>
                      <p><strong>Level:</strong> {selectedNote.education_level.toUpperCase()}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Curriculum Framework</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Strand:</strong> {selectedNote.strand}</p>
                    <p><strong>Sub-strand:</strong> {selectedNote.sub_strand}</p>
                    <p><strong>Content Standard:</strong> {selectedNote.content_standard}</p>
                    <p><strong>Indicator:</strong> {selectedNote.indicator}</p>
                  </div>
                </div>
                
                <div className="bg-white border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Generated Content</h4>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedNote.content || "Lesson content will be displayed here..."}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
            {selectedNote && (
              <div className="flex gap-2">
                {/* JHS Actions */}
                {selectedNote.education_level === 'jhs' && (
                  <>
                    <Button onClick={() => uploadToTeacherAccess(selectedNote)}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload to Teachers
                    </Button>
                    <Button variant="secondary" onClick={() => rewriteLessonNote(selectedNote)}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Rewrite
                    </Button>
                  </>
                )}
                
                {/* KG/Primary Actions */}
                {(selectedNote.education_level === 'kg' || selectedNote.education_level === 'primary') && (
                  <>
                    <Button variant="outline" onClick={() => downloadToClassFolder(selectedNote)}>
                      <Download className="h-4 w-4 mr-2" />
                      Download to Folder
                    </Button>
                    <Button variant="secondary" onClick={() => rewriteLessonNote(selectedNote)}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Rewrite
                    </Button>
                    <Button onClick={() => uploadToTeacherAccess(selectedNote)}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload to Teachers
                    </Button>
                  </>
                )}
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AILessonGenerator;